import React, { useRef, useEffect, useState } from 'react'

interface CustomScrollbarProps {
  children: React.ReactNode
  className?: string
  style?: React.CSSProperties
  onClick?: (e: React.MouseEvent) => void
}

export default function CustomScrollbar({
  children,
  className = '',
  style = {},
  onClick
}: CustomScrollbarProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [scrollInfo, setScrollInfo] = useState({
    scrollTop: 0,
    scrollHeight: 0,
    clientHeight: 0
  })

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const updateScrollInfo = () => {
      setScrollInfo({
        scrollTop: container.scrollTop,
        scrollHeight: container.scrollHeight,
        clientHeight: container.clientHeight
      })
    }

    updateScrollInfo()
    container.addEventListener('scroll', updateScrollInfo)
    window.addEventListener('resize', updateScrollInfo)

    return () => {
      container.removeEventListener('scroll', updateScrollInfo)
      window.removeEventListener('resize', updateScrollInfo)
    }
  }, [])

  const hasOverflow = scrollInfo.scrollHeight > scrollInfo.clientHeight
  const scrollProgress = hasOverflow ? scrollInfo.scrollTop / (scrollInfo.scrollHeight - scrollInfo.clientHeight) : 0

  return (
    <div
      ref={containerRef}
      className={`custom-scrollbar scroll-fade-effect ${className}`}
      style={{
        scrollbarWidth: 'auto',
        scrollbarColor: 'rgba(59, 130, 246, 0.6) rgba(59, 130, 246, 0.05)',
        position: 'relative',
        ...style
      }}
      onClick={onClick}
    >
      {children}

      {/* Subtle scroll indicator - only show when scrolling */}
      {hasOverflow && scrollProgress > 0.1 && (
        <div
          className="absolute right-1 top-1/2 transform -translate-y-1/2 w-0.5 h-12 bg-gradient-to-b from-blue-400/15 to-blue-600/15 rounded-full pointer-events-none z-30"
          style={{
            opacity: Math.min(scrollProgress * 1.5, 0.8),
            transform: `translateY(-50%) scaleY(${0.6 + scrollProgress * 0.4})`,
            transition: 'all 0.1s ease-out'
          }}
        >
          <div
            className="w-full bg-gradient-to-b from-blue-500/40 to-blue-600/40 rounded-full transition-all duration-300"
            style={{
              height: `${Math.max(15, scrollProgress * 100)}%`,
              boxShadow: `0 0 4px rgba(59, 130, 246, ${0.2 + scrollProgress * 0.3})`
            }}
          />
        </div>
      )}
    </div>
  )
}
